import React from "react";
import { fmtRelative } from "../lib/format.js";

const STATUS_COLORS = {
  // service case states grouped
  "Obra aberta": "neutral",
  "Recebido": "neutral",
  "Na fila da oficina": "neutral",
  "Em diagnóstico": "blue",
  "Aguarda classificação": "yellow",
  "Orçamento em preparação": "yellow",
  "Orçamento pronto": "yellow",
  "Aguarda envio ao cliente": "yellow",
  "Aguarda aprovação do cliente": "yellow",
  "Aprovado": "green",
  "Rejeitado pelo cliente": "red",
  "Aguarda pagamento ou PO": "yellow",
  "Aguarda pedido de peças": "yellow",
  "Aguarda peças": "yellow",
  "Peças parcialmente recebidas": "yellow",
  "Peças disponíveis": "green",
  "Entrada marcada": "blue",
  "Em reparação": "blue",
  "Controlo de qualidade": "blue",
  "Em lavagem": "blue",
  "Pronto para entrega": "green",
  "Entregue": "green",
  "Chamada de cortesia": "green",
  "Encerrado": "grey",
  "Cancelado": "grey",
  "Reaberto": "yellow",
  // task states
  "Aberta": "blue",
  "Em curso": "blue",
  "Aguarda terceiro": "yellow",
  "Concluída": "green",
  "Atrasada": "red",
  "Cancelada": "grey",
  "Reaberta": "yellow",
  // parts request states
  "Pedido criado": "blue",
  "Aguarda validação": "yellow",
  "Aguarda encomenda Alidata": "yellow",
  "Aguarda encomenda à fábrica": "yellow",
  "Encomendado": "blue",
  "Em trânsito": "blue",
  "Em despacho aduaneiro": "yellow",
  "Recebido parcial": "yellow",
  "Recebido completo": "green",
  "Cliente por contactar": "yellow",
  "Cliente contactado": "green",
  "Entrada agendada": "blue",
  "Marcação falhada": "red",
  "Entregue à oficina": "green",
  // classification request states (Pedido criado already mapped above for PR)
  "Pronto para classificar": "yellow",
  "Em classificação": "blue",
  "Aguarda informação da oficina": "yellow",
  "Classificado": "green",
  "Devolvido ao service advisor": "green",
  "Requote solicitado": "yellow",
  "Reclassificação em curso": "yellow",
  "Rejeitado": "red",
  // parts line statuses (kept English)
  Requested: "neutral",
  Ordered: "blue",
  "In transit": "blue",
  "Customs clearance": "yellow",
  Backorder: "yellow",
  "Received partial": "yellow",
  "Received complete": "green",
  Cancelled: "grey",
  Replaced: "yellow",
};

const CLASSES = {
  green: "bg-emerald-50 text-emerald-800 border-emerald-200",
  yellow: "bg-amber-50 text-amber-800 border-amber-200",
  red: "bg-red-50 text-red-800 border-red-200",
  grey: "bg-slate-100 text-slate-700 border-slate-300",
  blue: "bg-ronil-navy-50 text-ronil-navy border-ronil-navy-100",
  neutral: "bg-slate-50 text-slate-700 border-slate-200",
};

export default function StatusBadge({ status, color, className = "", maxWidth }) {
  if (!status) return null;
  const c = color || STATUS_COLORS[status] || "neutral";
  // Iter 62 · Fix — quando `maxWidth` é fornecido, truncar em overflow com
  // tooltip mostrando o texto completo. Usado no sticky header do SOM para
  // evitar que status longos (ex: "Peças parcialmente recebidas") empurrem
  // outros elementos.
  const truncateStyle = maxWidth ? { maxWidth, overflow: "hidden", textOverflow: "ellipsis" } : undefined;
  return (
    <span
      data-testid={`status-badge-${status}`}
      title={maxWidth ? status : undefined}
      style={truncateStyle}
      className={`inline-flex items-center px-2 py-0.5 rounded-md border text-[11px] font-heading font-semibold whitespace-nowrap tracking-tight ${CLASSES[c]} ${className}`}
    >
      {status}
    </span>
  );
}

export function FeedbackChip({ lastContactIso, showLabel = true, size = "sm" }) {
  const hours = lastContactIso
    ? (Date.now() - new Date(lastContactIso).getTime()) / 36e5
    : null;
  let bucket = "none";
  if (hours == null) bucket = "none";
  else if (hours < 48) bucket = "green";
  else if (hours < 72) bucket = "yellow";
  else bucket = "red";

  // Localised relative-time label via dayjs PT locale (set in lib/format.js)
  const label = lastContactIso ? fmtRelative(lastContactIso) : "Sem contacto registado";

  if (size === "lg") {
    // Prominent traffic-light chip for case header
    const styles = {
      green: { bg: "bg-[#28A745]", text: "text-white", dot: "bg-white" },
      yellow: { bg: "bg-[#FFC107]", text: "text-[#1A1A2E]", dot: "bg-[#1A1A2E]" },
      red: { bg: "bg-[#DC3545]", text: "text-white", dot: "bg-white" },
      none: { bg: "bg-[#6C757D]", text: "text-white", dot: "bg-white" },
    }[bucket];
    return (
      <span
        data-testid={`feedback-chip-${bucket}`}
        className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-sm border border-black/10 text-xs font-semibold ${styles.bg} ${styles.text}`}
        title={lastContactIso ? new Date(lastContactIso).toLocaleString() : ""}
      >
        <span className={`w-2 h-2 rounded-full ${styles.dot}`} />
        <span className="uppercase tracking-wider text-[10px] font-bold">Feedback</span>
        <span>·</span>
        <span className="font-medium">{label}</span>
      </span>
    );
  }

  // Default compact dot + label
  const dot = {
    green: "bg-[#28A745]",
    yellow: "bg-[#FFC107]",
    red: "bg-[#DC3545]",
    none: "bg-[#6C757D]",
  }[bucket];

  return (
    <span
      data-testid={`feedback-chip-${bucket}`}
      className="inline-flex items-center gap-1.5 text-xs text-[#1A1A2E]"
    >
      <span className={`w-2.5 h-2.5 rounded-full inline-block ${dot}`} />
      {showLabel && <span>{label}</span>}
    </span>
  );
}

export function EscalationBadge({ level }) {
  if (!level) return null;
  const colors = ["", "bg-[#FFE082]", "bg-[#FFB74D]", "bg-[#FF7043]", "bg-[#E53935]", "bg-[#1A1A2E]"];
  const text = level >= 5 ? "text-white" : "text-[#1A1A2E]";
  return (
    <span
      data-testid={`escalation-badge-${level}`}
      className={`inline-flex items-center font-mono text-[10px] font-bold px-1.5 py-0.5 rounded-sm ${colors[level]} ${text}`}
    >
      L{level}
    </span>
  );
}

export function PriorityBadge({ priority }) {
  if (!priority) return null;
  const colors = {
    Low: "bg-slate-50 text-slate-600 border-slate-200",
    Normal: "bg-ronil-navy-50 text-ronil-navy border-ronil-navy-100",
    High: "bg-amber-50 text-amber-800 border-amber-200",
    Urgent: "bg-orange-50 text-orange-800 border-orange-200",
    Critical: "bg-red-50 text-red-800 border-red-200",
  };
  return (
    <span
      data-testid={`priority-badge-${priority}`}
      className={`inline-flex items-center px-1.5 py-0.5 rounded-md border text-[10px] font-heading font-bold uppercase tracking-wider ${colors[priority] || colors.Normal}`}
    >
      {priority}
    </span>
  );
}
